/**
 * Geographic Configuration
 * 
 * Reads admin coordinates and coordinate validation limits from API settings
 * Falls back to hardcoded defaults if API is unavailable
 */

interface GeoSettings {
  admin_lat: number;
  admin_lng: number;
  coord_lat_min: number;
  coord_lat_max: number;
  coord_lng_min: number;
  coord_lng_max: number;
}

let cachedSettings: GeoSettings | null = null;

/**
 * Fetch geo settings from API
 */
async function fetchGeoSettings(): Promise<GeoSettings> {
  if (cachedSettings) {
    return cachedSettings;
  }

  try {
    const response = await fetch('/wp-json/boat-trader-v3/v1/settings');
    if (!response.ok) {
      throw new Error('Failed to fetch settings');
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error('Settings fetch failed');
    }
    
    const settings = result.data;
    cachedSettings = {
      admin_lat: parseFloat(settings.admin_lat) || 40.3516,
      admin_lng: parseFloat(settings.admin_lng) || 18.1723,
      coord_lat_min: parseFloat(settings.coord_lat_min) || 35.0,
      coord_lat_max: parseFloat(settings.coord_lat_max) || 48.0,
      coord_lng_min: parseFloat(settings.coord_lng_min) || 5.0,
      coord_lng_max: parseFloat(settings.coord_lng_max) || 20.0,
    };
    
    return cachedSettings;
  } catch (error) {
    console.error('Error fetching geo settings:', error);
    // Return defaults on error
    return {
      admin_lat: 40.3516,
      admin_lng: 18.1723,
      coord_lat_min: 35.0,
      coord_lat_max: 48.0,
      coord_lng_min: 5.0,
      coord_lng_max: 20.0,
    };
  }
}

/**
 * Check if geo settings are cached (synchronous)
 */
export const geoCacheReady = (): boolean => cachedSettings !== null;

/**
 * Get cached geo settings synchronously (null if not cached)
 */
export const getCachedGeoSettings = (): GeoSettings | null => cachedSettings;

/**
 * Get admin coordinates from settings (async, fetches if not cached)
 */
export const getAdminCoords = async () => {
  const settings = await fetchGeoSettings();
  return {
    lat: settings.admin_lat,
    lng: settings.admin_lng,
    name: 'Lecce (Admin)'
  };
};

/**
 * Get coordinate validation limits from settings
 */
export const getCoordLimits = async () => {
  const settings = await fetchGeoSettings();
  return {
    latMin: settings.coord_lat_min,
    latMax: settings.coord_lat_max,
    lngMin: settings.coord_lng_min,
    lngMax: settings.coord_lng_max,
  };
};

/**
 * Validate coordinates against settings limits
 */
export const validateCoords = async (lat: number, lng: number): Promise<boolean> => {
  const limits = await getCoordLimits();
  return (
    lat >= limits.latMin &&
    lat <= limits.latMax &&
    lng >= limits.lngMin &&
    lng <= limits.lngMax
  );
};

/**
 * Clear cached settings (useful for testing or after settings update)
 */
export const clearGeoSettingsCache = () => {
  cachedSettings = null;
};
